import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Users, Plus, Trash2, X, ChevronDown, ChevronUp, DollarSign } from 'lucide-react';
import { SwissCard } from '../layout/SwissUI';

const API = process.env.REACT_APP_BACKEND_URL;
const f = (n) => `$${Number(n || 0).toLocaleString()}`;
const EVENT_TYPES = ['Wedding', 'Corporate', 'Airport', 'Prom', 'Concert & Nightlife', 'VIP Experience'];

const AffiliateNetworkTab = () => {
  const [data, setData] = useState({ partners: [], total: 0, inbound_revenue: 0, outbound_volume: 0 });
  const [showForm, setShowForm] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({ name: '', type: '', contact_name: '', contact_email: '', contact_phone: '', commission_rate: 10 });
  const [bookingForm, setBookingForm] = useState({ direction: 'inbound', client_name: '', revenue: 0, event_type: '', notes: '' });

  const load = () => { fetch(`${API}/api/command-center/affiliate-network`).then(r => r.json()).then(setData); };
  useEffect(load, []);

  const create = async () => {
    if (!form.name) return;
    await fetch(`${API}/api/command-center/affiliate-network`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    setShowForm(false); setForm({ name: '', type: '', contact_name: '', contact_email: '', contact_phone: '', commission_rate: 10 }); load();
  };

  const logBooking = async () => {
    if (!showBookingForm) return;
    await fetch(`${API}/api/command-center/affiliate-network/${showBookingForm}/bookings`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(bookingForm) });
    setShowBookingForm(null); setBookingForm({ direction: 'inbound', client_name: '', revenue: 0, event_type: '', notes: '' }); load();
  };

  const del = async (id) => { if (!window.confirm('Delete this partner?')) return; await fetch(`${API}/api/command-center/affiliate-network/${id}`, { method: 'DELETE' }); load(); };

  return (
    <div data-testid="affiliate-network-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Users className="h-5 w-5 text-red-600" /> Affiliate Network</h2>
        <Button size="sm" onClick={() => setShowForm(true)} className="bg-red-600 hover:bg-red-700 text-xs h-8" data-testid="add-partner-btn"><Plus className="h-3 w-3 mr-1" /> Add Partner</Button>
      </div>

      {showForm && (
        <SwissCard className="p-4 mb-4">
          <div className="flex justify-between mb-3"><h3 className="text-sm font-bold">Add Partner</h3><button onClick={() => setShowForm(false)}><X className="h-4 w-4 text-gray-400" /></button></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Input placeholder="Partner / Company Name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="h-9 text-xs" data-testid="partner-name" />
            <Input placeholder="Type (e.g. Wedding Planner, Venue)" value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} className="h-9 text-xs" />
            <Input placeholder="Contact Name" value={form.contact_name} onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))} className="h-9 text-xs" />
            <Input placeholder="Email" value={form.contact_email} onChange={e => setForm(p => ({ ...p, contact_email: e.target.value }))} className="h-9 text-xs" />
            <Input placeholder="Phone" value={form.contact_phone} onChange={e => setForm(p => ({ ...p, contact_phone: e.target.value }))} className="h-9 text-xs" />
            <Input type="number" placeholder="Commission %" value={form.commission_rate || ''} onChange={e => setForm(p => ({ ...p, commission_rate: parseFloat(e.target.value) || 0 }))} className="h-9 text-xs" />
          </div>
          <div className="flex gap-2 mt-3"><Button size="sm" onClick={create} className="bg-red-600 hover:bg-red-700 text-xs h-8">Add Partner</Button><Button size="sm" variant="outline" onClick={() => setShowForm(false)} className="text-xs h-8">Cancel</Button></div>
        </SwissCard>
      )}

      <div className="grid grid-cols-3 gap-3 mb-4">
        <SwissCard className="p-3 text-center"><p className="text-lg font-bold text-gray-900">{data.total}</p><p className="text-[10px] text-gray-500">Active Partners</p></SwissCard>
        <SwissCard className="p-3 text-center"><p className="text-lg font-bold text-emerald-600">{f(data.inbound_revenue)}</p><p className="text-[10px] text-gray-500">Inbound Revenue</p></SwissCard>
        <SwissCard className="p-3 text-center"><p className="text-lg font-bold text-blue-600">{f(data.outbound_volume)}</p><p className="text-[10px] text-gray-500">Outbound Volume</p></SwissCard>
      </div>

      {/* Booking Log Form */}
      {showBookingForm && (
        <SwissCard className="p-4 mb-4">
          <div className="flex justify-between mb-3"><h3 className="text-sm font-bold">Log a Booking</h3><button onClick={() => setShowBookingForm(null)}><X className="h-4 w-4 text-gray-400" /></button></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="flex gap-1">
              {['inbound', 'outbound'].map(d => (
                <button key={d} onClick={() => setBookingForm(p => ({ ...p, direction: d }))} className={`flex-1 h-9 text-[10px] font-bold rounded border capitalize ${bookingForm.direction === d ? 'bg-black text-white border-black' : 'border-gray-200'}`}>
                  {d === 'inbound' ? 'Inbound Referral' : 'Outbound Farm-Out'}
                </button>
              ))}
            </div>
            <Input placeholder="Client Name" value={bookingForm.client_name} onChange={e => setBookingForm(p => ({ ...p, client_name: e.target.value }))} className="h-9 text-xs" />
            <Input type="number" placeholder="Revenue $" value={bookingForm.revenue || ''} onChange={e => setBookingForm(p => ({ ...p, revenue: parseFloat(e.target.value) || 0 }))} className="h-9 text-xs" />
            <Select value={bookingForm.event_type} onValueChange={v => setBookingForm(p => ({ ...p, event_type: v }))}><SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Event Type" /></SelectTrigger><SelectContent>{EVENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            <Input placeholder="Notes" value={bookingForm.notes} onChange={e => setBookingForm(p => ({ ...p, notes: e.target.value }))} className="h-9 text-xs sm:col-span-2" />
          </div>
          <div className="flex gap-2 mt-3"><Button size="sm" onClick={logBooking} className="bg-red-600 hover:bg-red-700 text-xs h-8">Log Booking</Button><Button size="sm" variant="outline" onClick={() => setShowBookingForm(null)} className="text-xs h-8">Cancel</Button></div>
        </SwissCard>
      )}

      <h3 className="text-sm font-bold text-gray-700 mb-2">Partner Directory</h3>
      {data.partners.length === 0 ? (
        <SwissCard className="p-8 text-center"><Users className="h-8 w-8 text-gray-300 mx-auto mb-2" /><p className="text-sm text-gray-400">No affiliate partners yet.</p></SwissCard>
      ) : (
        <div className="space-y-2">
          {data.partners.map(p => {
            const isExpanded = expandedId === p.partner_id;
            return (
              <SwissCard key={p.partner_id} className="overflow-hidden">
                <button className="w-full p-3 text-left flex items-center justify-between" onClick={() => setExpandedId(isExpanded ? null : p.partner_id)}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center text-xs font-bold text-red-600">{p.name?.charAt(0)}</div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">{p.name}</p>
                      <p className="text-[10px] text-gray-500">{p.type || 'Partner'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-[10px]">
                      <span className="text-emerald-600 font-bold">{f(p.inbound_revenue || 0)} In</span>
                      <span className="text-gray-400 mx-1">|</span>
                      <span className="text-blue-600 font-bold">{f(p.outbound_volume || 0)} Out</span>
                      <span className="text-gray-400 mx-1">|</span>
                      <span className="text-gray-600">{p.total_bookings || 0} Bookings</span>
                    </div>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3 border-t border-gray-100">
                    <div className="flex items-center justify-between mt-2 mb-2">
                      <div className="text-[10px] text-gray-500 space-x-3">
                        {p.contact_name && <span>{p.contact_name}</span>}
                        {p.contact_email && <span>{p.contact_email}</span>}
                        {p.contact_phone && <span>{p.contact_phone}</span>}
                        {p.commission_rate > 0 && <span className="font-semibold">Commission: {p.commission_rate}%</span>}
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" onClick={() => setShowBookingForm(p.partner_id)} className="bg-emerald-600 hover:bg-emerald-700 text-[10px] h-7 px-2"><Plus className="h-3 w-3 mr-0.5" /> Log Booking</Button>
                        <Button size="sm" variant="outline" onClick={() => del(p.partner_id)} className="text-[10px] h-7 px-2 text-red-500"><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                    {(p.bookings || []).length === 0 ? (
                      <p className="text-[10px] text-gray-400 py-2">No bookings logged yet.</p>
                    ) : (
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {(p.bookings || []).slice().reverse().map((b, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-[10px]">
                            <div className="flex items-center gap-2">
                              <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${b.direction === 'inbound' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{b.direction === 'inbound' ? 'IN' : 'OUT'}</span>
                              <span className="font-semibold">{b.client_name || '—'}</span>
                              <span className="text-gray-400">{b.event_type}</span>
                            </div>
                            <span className="font-bold">{f(b.revenue)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </SwissCard>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AffiliateNetworkTab;
